
import { motion } from "framer-motion";
import { Rocket, Search, Handshake, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { whyChooseTranslations } from "@/utils/translations/whyChoose";
import { localizePath } from "@/utils/languageRoutes";

interface WhyChooseSectionProps {
  language: string;
}

const WhyChooseSection = ({ language }: WhyChooseSectionProps) => {
  const navigate = useNavigate();
  const t = whyChooseTranslations[language as keyof typeof whyChooseTranslations];

  const cardStyles = [
    {
      Icon: Rocket,
      gradient: "from-blue-500 to-indigo-600",
      glow: "group-hover:shadow-blue-500/25",
    },
    {
      Icon: Search,
      gradient: "from-emerald-500 to-green-600",
      glow: "group-hover:shadow-emerald-500/25",
    },
    {
      Icon: Handshake,
      gradient: "from-amber-500 to-orange-500",
      glow: "group-hover:shadow-amber-500/25",
    },
    {
      Icon: Globe,
      gradient: "from-violet-500 to-purple-600",
      glow: "group-hover:shadow-violet-500/25",
    },
  ];

  return (
    <section className="relative overflow-hidden py-20 bg-gradient-to-b from-white via-sky-50/40 to-white">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-secondary/5 blur-3xl" />

      <div className="relative container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-14"
        >
          <span className="inline-flex items-center rounded-full bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary mb-4">
            {language === "KO" ? "두루의 강점" : "Why Duru"}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-dark mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
          {t.differentiators.map((item, index) => {
            const style = cardStyles[index];
            const Icon = style.Icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }}
                className={`group relative overflow-hidden rounded-2xl bg-white p-7 border border-neutral-100/80 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl ${style.glow}`}
              >
                <div className={`absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r ${style.gradient} transition-transform duration-300 group-hover:scale-x-100`} />

                <div className={`relative mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${style.gradient} shadow-lg ring-4 ring-white transition-transform duration-300 group-hover:scale-110`}>
                  <Icon className="text-white" size={26} strokeWidth={2} />
                </div>

                <h3 className="text-lg font-bold text-neutral-dark mb-2.5">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-600">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
        >
          <Button
            onClick={() => navigate(localizePath('/contact', language))}
            size="lg"
            className="bg-primary hover:bg-primary-light text-white font-medium shadow-md hover:shadow-lg transition-all duration-300"
          >
            {t.cta}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
